/**
 * Authentication API Service
 *
 * API methods for authentication operations including login, password reset,
 * token refresh, and session management.
 *
 * Uses dual-token system (15-minute access tokens + 7-day refresh tokens).
 * Updated to use standardized API response format.
 */

import apiClient from './client';
import type { ApiSuccessResponse, ApiCollectionResponse, ApiActionResponse } from '../types/api.types';
import type {
  LoginRequest,
  LoginStep1Response,
  VerifyLoginRequest,
  ResendLoginCodeRequest,
  LoginResponse,
  TokenRefreshResponse,
  PasswordResetRequest,
  PasswordResetConfirm,
  SessionSanitized,
  RoleSessionStatusResponse,
  RoleSessionActivationResponse,
  RoleSessionDeactivationResponse,
  TwoFactorRequiredResponse,
} from '../types/auth.types';
import { clearAllTokens, getRefreshToken } from '../lib/utils/token';

// ============================================================================
// AUTHENTICATION SERVICE
// ============================================================================

export const authService = {
  // ==========================================================================
  // LOGIN METHODS (2-Step with 2FA)
  // ==========================================================================

  /**
   * Login Step 1: Verify credentials and send 2FA code
   * Returns 2FA challenge with codeId
   */
  loginStep1: async (data: LoginRequest): Promise<LoginStep1Response> => {
    const response = await apiClient.post<ApiSuccessResponse<LoginStep1Response>>('/auth/login', data);
    return response.data.data;
  },

  /**
   * Login Step 2: Verify 2FA code and complete login
   * Returns access and refresh tokens on success
   */
  verifyLogin: async (data: VerifyLoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<ApiSuccessResponse<LoginResponse>>('/auth/verify-login', data);
    return response.data.data;
  },

  /**
   * Resend 2FA code for login
   * Returns new codeId and expiry info
   */
  resendLoginCode: async (data: ResendLoginCodeRequest): Promise<LoginStep1Response> => {
    const response = await apiClient.post<ApiSuccessResponse<LoginStep1Response>>('/auth/resend-login-code', data);
    return response.data.data;
  },

  // ==========================================================================
  // TOKEN REFRESH METHODS
  // ==========================================================================

  /**
   * Refresh tokens using refresh token
   * Returns new access and refresh tokens (token rotation)
   */
  refresh: async (): Promise<TokenRefreshResponse> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiClient.post<ApiSuccessResponse<TokenRefreshResponse>>(
      '/auth/refresh',
      { refreshToken }
    );
    return response.data.data;
  },

  // ==========================================================================
  // LOGOUT METHODS
  // ==========================================================================

  /**
   * Logout and revoke current session on server
   * Also clears local storage tokens
   */
  logout: async (): Promise<void> => {
    try {
      // Try to revoke session on server
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Ignore errors - we still want to clear local tokens
      console.warn('[Auth] Server logout failed, clearing local tokens anyway:', error);
    } finally {
      // Always clear local tokens
      clearAllTokens();
      localStorage.removeItem('user_data');
      localStorage.removeItem('tenant_data');
    }
  },

  // ==========================================================================
  // SESSION MANAGEMENT METHODS
  // ==========================================================================

  /**
   * List all active sessions for current user
   */
  listSessions: async (): Promise<SessionSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<SessionSanitized>>('/auth/sessions');
    return response.data.data;
  },

  /**
   * Revoke a specific session by ID
   * @param sessionId - Session ID to revoke
   */
  revokeSession: async (sessionId: string): Promise<void> => {
    await apiClient.delete(`/auth/sessions/${sessionId}`);
  },

  /**
   * Revoke all sessions except current (logout from all other devices)
   * @param keepCurrentSession - If true, keeps current session active
   */
  revokeAllSessions: async (keepCurrentSession: boolean = true): Promise<{ revokedCount: number }> => {
    const response = await apiClient.post<ApiActionResponse>('/auth/sessions/revoke-all', { keepCurrentSession });
    return { revokedCount: (response.data.data?.revokedCount as number) || 0 };
  },

  // ==========================================================================
  // PASSWORD RESET METHODS
  // ==========================================================================

  /**
   * Request password reset email
   */
  requestPasswordReset: async (data: PasswordResetRequest): Promise<{ message: string }> => {
    const response = await apiClient.post<ApiActionResponse>('/auth/password-reset/request', data);
    return { message: response.data.message };
  },

  /**
   * Confirm password reset with token and new password
   */
  resetPassword: async (data: PasswordResetConfirm): Promise<{ message: string }> => {
    const response = await apiClient.post<ApiActionResponse>('/auth/password-reset/confirm', data);
    return { message: response.data.message };
  },

  // ==========================================================================
  // ROLE SESSION METHODS
  // ==========================================================================

  /**
   * Get role session status
   * @param tenantId - Tenant ID
   */
  getRoleSessionStatus: async (tenantId: string): Promise<RoleSessionStatusResponse> => {
    const response = await apiClient.get<ApiSuccessResponse<RoleSessionStatusResponse>>(
      `/tenants/${tenantId}/role-session/status`
    );
    return response.data.data;
  },

  /**
   * Activate role session
   * @param tenantId - Tenant ID
   * @param tenantRoleId - The ID of the tenant role to activate
   * @param durationHours - Desired session duration in hours (1-8, defaults to tenant setting)
   * @param verificationCodeId - Optional verification code ID for 2FA
   * @param verificationCode - Optional verification code for 2FA
   */
  activateRoleSession: async (
    tenantId: string,
    tenantRoleId: string,
    durationHours?: number,
    verificationCodeId?: string,
    verificationCode?: string
  ): Promise<TwoFactorRequiredResponse | RoleSessionActivationResponse> => {
    const response = await apiClient.post<ApiSuccessResponse<TwoFactorRequiredResponse | RoleSessionActivationResponse>>(
      `/tenants/${tenantId}/role-session/activate`,
      { tenantRoleId, durationHours, verificationCodeId, verificationCode }
    );
    return response.data.data;
  },

  /**
   * Deactivate role session
   * @param tenantId - Tenant ID
   */
  deactivateRoleSession: async (tenantId: string): Promise<RoleSessionDeactivationResponse> => {
    const response = await apiClient.post<ApiSuccessResponse<RoleSessionDeactivationResponse>>(
      `/tenants/${tenantId}/role-session/deactivate`
    );
    return response.data.data;
  },
};

export default authService;
